// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import { CustomAppState } from "@gcg/App/Store/Reducers";
import Button from "@gcg/MobiusOverrides/Button";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import {
    canCheckoutWithCart,
    canSubmitForQuote,
    canSubmitRequisition,
    getCurrentCartState,
    hasOnlyQuoteRequiredProducts,
    hasQuoteRequiredProducts,
    isCartCheckoutDisabled,
    isCartEmpty,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import doPunchOutCheckout from "@insite/client-framework/Store/Pages/Cart/Handlers/DoPunchOutCheckout";
import submitRequisition from "@insite/client-framework/Store/Pages/Cart/Handlers/SubmitRequisition";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Checkbox from "@insite/mobius/Checkbox/Checkbox";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { NewsLetterContext } from "@seacoastusa/App/Pages/CustomCartPage";
import CheckboxGroup from "@seacoastusa/MobiusOverrides/CheckboxGroup/CheckboxGroup";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState & ApplicationState) => {
    const settingsCollection = getSettingsCollection(state);

    const cartState = getCurrentCartState(state);
    const cart = cartState.value;

    return {
        isLoading: cartState.isLoading,
        cart,
        cartSettings: settingsCollection.cartSettings,
        canCheckoutWithCart: canCheckoutWithCart(cart),
        isCartCheckoutDisabled: !cart || isCartCheckoutDisabled(cart),
        isCartEmpty: isCartEmpty(cart),
        hasOnlyQuoteRequiredProducts: hasOnlyQuoteRequiredProducts(cart),
        hasQuoteRequiredProducts: hasQuoteRequiredProducts(cart),
        canSubmitForQuote: canSubmitForQuote(cart),
        rfqRequestQuotePageUrl: getPageLinkByPageType(state, "RfqRequestQuotePage")?.url,
        canSubmitRequisition: canSubmitRequisition(cart),
        requisitionConfirmationPageUrl: getPageLinkByPageType(state, "RequisitionConfirmationPage")?.url,
        customSettings: getSettingsCollection(state).customSettings,
        isSubmittingCart: state.xCustomCart.isSubmittingCart,
    };
};

const mapDispatchToProps = {
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    cancelPunchOut,
    submitRequisition,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & HasHistory & ResolveThunks<typeof mapDispatchToProps>;

export interface CustomCartTotalStyles {
    wrappingContainer?: InjectableCss;
    container?: InjectableCss;
    wrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
}

export const customCartTotalStyles: CustomCartTotalStyles = {
    wrappingContainer: {
        css: css`
            width: 100%;
        `,
    },
    container: {
        css: css`
            padding-bottom: 15px;
            width: 100%;
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            padding-top: 15px;
            width: 100%;
        `,
    },
    titleWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            border-bottom: 1px solid ${getColor("text.main")};
            padding-bottom: 15px;

            & h3 {
                font-size: 26px;
                line-height: 32px;
                font-weight: bold;
            }

            & span {
                display: block;
                font-size: 14px;
                line-height: 17px;
            }
        `,
    },
};

const styles = customCartTotalStyles;

const CustomCartTotal: FC<Props> = ({
    customSettings,
    cartSettings,
    history,
    isCartEmpty,
    isLoading,
    cart,
    rfqRequestQuotePageUrl,
    isSubmittingCart,
}) => {
    const submitForQuoteClickHandler = () => {
        if (rfqRequestQuotePageUrl) {
            history.push(rfqRequestQuotePageUrl);
        }
    };

    const isActionsDisabled = isLoading || !cart || isCartEmpty;

    const { checked, onToggleNewsLetterSubs } = React.useContext(NewsLetterContext);
    return (
        <>
            {!isCartEmpty && (
                <StyledWrapper
                    css={css`
                        @media print {
                            display: none;
                        }
                    `}
                >
                    <StyledWrapper {...styles.wrappingContainer}>
                        <StyledWrapper {...styles.container} data-test-selector={`cartTotal_${cart?.id}`}>
                            <StyledWrapper {...styles.titleWrapper}>
                                <Typography as="h3">{translate("Quote Summary")}</Typography>
                                <StyledWrapper
                                    css={css`
                                        display: inline-flex;
                                        line-height: 17px;
                                    `}
                                >
                                    <Typography as="span">
                                        {cart?.cartLines?.length} {translate("Items")}
                                    </Typography>
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                        <CheckboxGroup>
                            <Checkbox
                                onChange={(e, value) => {
                                    onToggleNewsLetterSubs(value);
                                }}
                                checked={checked}
                                data-test-selector="requestQuoteForm_subscription"
                            >
                                {siteMessage("SignIn_Sign_Up_Newsletter")}
                            </Checkbox>
                        </CheckboxGroup>
                    </StyledWrapper>
                    <StyledWrapper
                        css={css`
                            & button {
                                width: 100%;
                                margin-top: 25px;
                            }
                        `}
                    >
                        <Button
                            variant="primary"
                            disabled={cart?.cartNotPriced || isActionsDisabled || isSubmittingCart}
                            type="submit"
                            data-test-selector="cartTotal_submitCustomRFQ"
                            form="custom-rfq-form"
                        >
                            {translate("Submit RFQ")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CustomCartTotal)),
    definition: {
        group: "Cart",
        icon: "cart-shopping",
        allowedContexts: ["CustomCartPage"],
    },
};

export default widgetModule;
